import React, { memo, useEffect, useRef, useState } from 'react';
import { Close, DoneAll } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useInfiniteNotificationsQuery, useUpdateNotificationsMutation } from 'store/services/notification';
import { convertToLocal24 } from 'utils/time-conversion';
import { useTheme } from '@mui/material/styles';
import useAuth from 'hooks/useAuth';
import { GLOBAL_DATE_FORMAT } from 'constant';

function CNotificationCard({ notification, final, onClick }) {
    const theme = useTheme();
    const { id, message, created_on: date } = notification || {};

    const handleClick = () => {
        onClick(notification);
    };

    return (
        <>
            <Stack
                direction="row"
                onClick={handleClick}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                }}
            >
                <Stack direction="row" spacing={2} sx={{
                    alignItems: 'center'
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textTransform: 'capitalize',
                            height: 80,
                            width: 80,
                            borderRadius: 80,
                            background: theme.palette.grey[300],
                            fontSize: 30,
                            fontWeight: 600,
                            flexShrink: 0
                        }}
                    >
                        {message?.charAt(0) || 'N'}
                    </Box>
                    <Stack spacing={1}>
                        <Typography>{message}</Typography>
                        <Typography variant="subtitle2">{convertToLocal24(date, GLOBAL_DATE_FORMAT)}</Typography>
                    </Stack>
                </Stack>
            </Stack>
            {!final && <Divider />}
        </>
    );
}

function NotificationDrawer({ open, onClose, onClick }) {
    const { user } = useAuth();
    const observerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const [page, setPage] = useState(1);

    const [readNotification] = useUpdateNotificationsMutation();

    const { data: notifications, isFetching } = useInfiniteNotificationsQuery(
        {
            filters: {
                ...(user?.client_id && { client_id: user.client_id })
            },
            pagination: { current_page: page }
        },
        { skip: !open }
    );

    const lastPage = notifications?.pagination?.last_page || 1;
    const loadedPage = notifications?.pagination?.current_page || 1;

    useEffect(() => {
        if (!open) {
            setPage(1);
            return;
        }

        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [open]);

    const handleMarkAllRead = () => {
        const unseen = notifications?.fields?.filter((n) => !n.is_read);
        readNotification({
            ...(unseen?.length > 0 && {
                notification_ids: unseen?.map((n) => n.id) || []
            })
        });
    };

    useEffect(() => {
        if (!open || !scrollContainerRef.current || !loadMoreRef.current) {
            return undefined;
        }

        const options = {
            root: scrollContainerRef.current,
            rootMargin: '0px 0px 120px 0px',
            threshold: 0.1
        };

        const handleIntersect = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !isFetching && loadedPage === page && page < lastPage) {
                    setPage((page) => page + 1);
                }
            });
        };

        observerRef.current = new IntersectionObserver(handleIntersect, options);
        observerRef.current.observe(loadMoreRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [isFetching, lastPage, loadedPage, open, page]);

    return (
        <Drawer open={open} anchor="right" onClose={onClose}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 500,
                    height: '100vh',
                    px: 1,
                    mt: 1
                }}
            >
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        height: 60,
                        px: 1
                    }}>
                    <Typography variant="h4">Notifications</Typography>
                    <Tooltip title="Close notification drawer">
                        <IconButton onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Divider />
                <Stack ref={scrollContainerRef} spacing={1} sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                    {notifications?.fields?.map((n, idx) => (
                        <CNotificationCard
                            key={n.id}
                            notification={n}
                            final={idx === notifications?.fields?.length - 1}
                            onClick={onClick}
                        />
                    ))}
                    <Box ref={loadMoreRef} sx={{ height: 1, width: '100%' }} />
                </Stack>
                <Divider />
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1
                    }}>
                    <Button variant="text" onClick={handleMarkAllRead}>
                        <DoneAll sx={{ fontSize: 24, mr: 1 }} />
                        Mark all as read
                    </Button>
                </Stack>
            </Box>
        </Drawer>
    );
}

CNotificationCard.propTypes = {
    notification: PropTypes.object,
    final: PropTypes.bool,
    onClick: PropTypes.func
};

NotificationDrawer.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onClick: PropTypes.func
};

export default memo(NotificationDrawer);
